/**
 * Hora de Britannia (techspec-dia-e-noite). Quem manda é o servidor: ele manda o minuto do dia (0..1440) e quantos
 * ms reais vale um minuto de Britannia; o cliente anda daí com o próprio relógio. Sem servidor (modo de teste), a hora
 * sai da mesma conta do servidor sem ajuste do GM: minuto = (unixMs / msPorMinuto) mod 1440.
 *
 * 5 s reais por minuto de Britannia, o tempo da UO: um dia inteiro em 2 h.
 */
import type { Idioma } from '../i18n/idioma';

export const MS_POR_MINUTO = 5000;
const DIA = 1440;

let base: { minuto: number; em: number; msPorMinuto: number } | null = null;
const ouvintes = new Set<() => void>();

export function sincronizarHora(minuto: number, msPorMinuto: number): void {
  if (!Number.isFinite(minuto) || !Number.isFinite(msPorMinuto) || msPorMinuto <= 0) return;
  base = { minuto: ((minuto % DIA) + DIA) % DIA, em: performance.now(), msPorMinuto };
  ouvintes.forEach(f => f());
}

/** O minuto do dia agora, com fração (0 ≤ m < 1440). */
export function minutoAgora(): number {
  if (!base) return (Date.now() / MS_POR_MINUTO) % DIA;
  return (base.minuto + (performance.now() - base.em) / base.msPorMinuto) % DIA;
}

/** Quanto tempo real dura um minuto de Britannia (para quem quer atualizar a tela no ritmo). */
export function msPorMinutoAtual(): number {
  return base?.msPorMinuto ?? MS_POR_MINUTO;
}

export function aoSincronizarHora(f: () => void): () => void {
  ouvintes.add(f);
  return () => { ouvintes.delete(f); };
}

/**
 * "18:40" em português; "6:40 PM" (relógio de 12 h) nos outros idiomas, como o Renato pediu.
 */
export function formatarHora(minuto: number, idioma: Idioma): string {
  const h = Math.floor(minuto / 60) % 24;
  const m = Math.floor(minuto % 60);
  return new Intl.DateTimeFormat(idioma, { hour: 'numeric', minute: '2-digit', hour12: idioma !== 'pt-BR', timeZone: 'UTC' })
    .format(new Date(Date.UTC(2000, 0, 1, h, m)));
}

/* ---------- a curva de luz (a "proposta" do protótipo) ---------- */

/** Nível de luz da UO: 0 é claro, 30 é escuro. A noite fica no 26, como a UO. */
export const NOITE = 26;

/** Mapas que ficam sempre no escuro da noite, com as luzes acesas (decisão do Renato, 22/10). */
export const MAPAS_ESCUROS: ReadonlySet<string> = new Set(['fire-dungeon', 'ice-dungeon']);

const suave = (t: number) => t * t * (3 - 2 * t);

/** Amanhecer das 5h às 7h, dia das 7h às 18h, entardecer das 18h às 20h, noite das 20h às 5h. */
export function nivelDeLuz(minuto: number): number {
  const h = minuto / 60;
  if (h < 5) return NOITE;
  if (h < 7) return NOITE * (1 - suave((h - 5) / 2));
  if (h < 18) return 0;
  if (h < 20) return NOITE * suave((h - 18) / 2);
  return NOITE;
}

/** A cor do ambiente (0..1 por canal) que multiplica o mundo: azulada à noite, laranja no entardecer. */
export function corAmbiente(minuto: number, nivel: number): readonly [number, number, number] {
  const n = nivel / 30;
  const b = 1 - n * 0.82;
  let r = b * (1 - n * 0.25);
  let g = b * (1 - n * 0.12);
  let bl = Math.min(1, b * (1 + n * 0.35));
  const h = minuto / 60;
  const quente = Math.max(0, 1 - Math.abs(h - 18.6) / 1.4) + Math.max(0, 1 - Math.abs(h - 6.2) / 1.2) * 0.7;
  r = Math.min(1, r + quente * 0.22);
  g = Math.min(1, g + quente * 0.06);
  bl = bl * (1 - quente * 0.22);
  return [r, g, bl];
}
